import { Chip, useTheme } from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { FunctionComponent, useContext, useEffect } from 'react';
import { AppContext } from '../../../core/config';
import { useAppTranslate } from '../../../core/hooks';
import { NetworkServiceUpdateEvent } from '../../../l3-services/model';
import { Loader } from '../../../ui/components';
import { useDashboardItsmNetworkServiceIncidents } from '../../hooks';
import { DashboardCard } from '../DashboardCard/DashboardCard';
import { useLocalizedDateFormat } from '../../../core/hooks/DateFormat/DateFormat';

interface Props {
  incidentsUpdates: NetworkServiceUpdateEvent[];
}

const options: MUIDataTableOptions = {
  enableNestedDataAccess: '.',
  selectToolbarPlacement: 'none',
  download: false,
  print: false,
  rowHover: false,
  expandableRows: false,
  selectableRows: 'none',
  expandableRowsHeader: false,
  filter: false,
  search: false,
  viewColumns: false,
  pagination: false,
};

export const DashboardNotifications: FunctionComponent<Props> = ({ incidentsUpdates }) => {
  const translate = useAppTranslate();
  const { customerId } = useContext(AppContext);
  const { data, isFetched, isFetching, refetch } = useDashboardItsmNetworkServiceIncidents(customerId);
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const localizedDateFormat = useLocalizedDateFormat();
  const renderDate = (date?: string): string => date ? localizedDateFormat.format(new Date(date)) : '-';

  useEffect(() => {
    if (customerId !== null && !isFetching) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentsUpdates, refetch]);

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'itsmId',
      label: translate('cards.notifications.itsmId'),
    },
    {
      name: 'status',
      label: translate('cards.notifications.status'),
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <Chip
            label={data && data[dataIndex].status}
            sx={{ background: '#D7EDFF', color: primaryColor }}
          />;
        },
      },
    },
    {
      name: 'sozOrderNumber',
      label: translate('cards.notifications.sozOrderNumber'),
    },
    {
      name: 'serviceName',
      label: translate('cards.notifications.serviceName'),
    },
    {
      name: 'reportingChannel',
      label: translate('cards.notifications.reportingChannel'),
    },
    {
      name: 'creationTime',
      label: translate('cards.notifications.creationTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(data?.[dataIndex].creationTime);
        },
      },
    },
    {
      name: 'lastUpdateTime',
      label: translate('cards.notifications.lastUpdateTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(data?.[dataIndex].lastUpdateTime);
        },
      },
    },
    {
      name: 'resolutionTime',
      label: translate('cards.notifications.resolutionTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(data?.[dataIndex].resolutionTime);
        },
      },
    },
  ];

  return <DashboardCard title={translate('cards.notifications.title')}>
    {isFetching ? (<Loader loading={true} />) : null}
    {!isFetching && isFetched && data ? (
      <MUIDataTable
        title={''}
        columns={columns}
        data={data}
        options={options}
      />
    ) : null}
  </DashboardCard>;
};